import { Component } from '@theme/component';

/**
 * Inline size selector for product cards.
 *
 * Adds the chosen size via the theme's product-form-component (requestSubmit),
 * so it reuses the same add-to-cart flow (and cart popup) as the rest of the theme.
 *
 * @extends Component
 */
class InlineSizeAdd extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('cart:update', this.#onCartUpdate);
    window.removeEventListener('click', this.#onOutsideClick, true);
  }

  /** @param {Event} event */
  openSheet(event) {
    event.preventDefault();
    const sheet = this.#sheet;
    if (!sheet || sheet.open) return;
    sheet.showModal();
    window.addEventListener('click', this.#onOutsideClick, true);

    const initial = this.querySelector('.inline-size-add__option:not(:disabled)');
    if (initial instanceof HTMLButtonElement) this.#applySelection(initial);
  }

  /** @param {Event} [event] */
  closeSheet(event) {
    event?.preventDefault();
    window.removeEventListener('click', this.#onOutsideClick, true);
    this.#sheet?.close();
  }

  /**
   * Desktop adds immediately. Mobile selects the size so price/stock can update first.
   * @param {Event} event
   */
  onOptionClick(event) {
    event.preventDefault();
    const button = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    if (this.classList.contains('inline-size-add--mobile')) {
      this.#applySelection(button);
      return;
    }

    this.#addWithButton(button);
  }

  /** @param {Event} event */
  submitSelected(event) {
    event.preventDefault();
    const selected = this.querySelector('.inline-size-add__option--selected');
    if (!(selected instanceof HTMLButtonElement) || selected.disabled) return;
    this.#addWithButton(selected);
  }

  /** @param {HTMLButtonElement} button */
  #applySelection(button) {
    this.querySelectorAll('.inline-size-add__option--selected').forEach((option) => {
      option.classList.remove('inline-size-add__option--selected');
      option.setAttribute('aria-pressed', 'false');
    });

    button.classList.add('inline-size-add__option--selected');
    button.setAttribute('aria-pressed', 'true');

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && button.dataset.variantId) {
      variantInput.value = button.dataset.variantId;
    }

    this.#updatePrice(button);
    this.#updateStock(button);

    const addButton = this.querySelector('.inline-size-add__sheet-add');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = false;
  }

  /** @param {HTMLButtonElement} button */
  #updateStock(button) {
    const stock = this.querySelector('.inline-size-add__sheet-info .inline-size-add__stock');
    if (!(stock instanceof HTMLElement)) return;

    const lowStock = button.dataset.lowStock === 'true';
    const quantity = button.dataset.inventory;

    if (lowStock && quantity) {
      stock.textContent = `Quedan ${quantity}`;
      stock.hidden = false;
    } else {
      stock.textContent = '';
      stock.hidden = true;
    }
  }

  /** @param {HTMLButtonElement} button */
  #updatePrice(button) {
    const priceEl = this.querySelector('.inline-size-add__price');
    if (!(priceEl instanceof HTMLElement)) return;

    const price = button.dataset.price || '';
    const compareAt = button.dataset.compareAt || '';
    const onSale = button.dataset.onSale === 'true';

    if (onSale && compareAt) {
      priceEl.replaceChildren();
      const compare = document.createElement('s');
      compare.textContent = compareAt;
      priceEl.append(compare, ' ');
      const sale = document.createElement('span');
      sale.className = 'inline-size-add__price-sale';
      sale.textContent = price;
      priceEl.append(sale);
      return;
    }

    priceEl.textContent = price;
  }

  /** @param {HTMLButtonElement} button */
  #addWithButton(button) {
    const variantInput = this.querySelector('input[name="id"]');
    const form = this.querySelector('form');
    if (!button.dataset.variantId || !(variantInput instanceof HTMLInputElement) || !form) return;

    variantInput.value = button.dataset.variantId;
    form.requestSubmit();
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    this.closeSheet();
  };

  /**
   * Close on backdrop or the close icon. Clicks inside the sheet stay in the sheet
   * so the shopper can change size and read remaining stock.
   * @param {MouseEvent} event
   */
  #onOutsideClick = (event) => {
    if (!this.#sheet?.open) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.inline-size-add__sheet-panel') && !event.target.closest('.inline-size-add__sheet-close')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.closeSheet();
  };

  /** @returns {HTMLDialogElement | null} */
  get #sheet() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
  }
}

if (!customElements.get('inline-size-add')) {
  customElements.define('inline-size-add', InlineSizeAdd);
}
